import type {PictureField} from "@buildwithflux/accounts-schema";

export interface AvatarCdnConfig {
    readonly imgCdnUrl: string;
    readonly bucket: string;
}

export const DEFAULT_CDN_CONFIG: AvatarCdnConfig = {
    imgCdnUrl: "https://img-cdn.flux.ai",
    bucket: "graviton-electric-symbols",
};

export type AvatarFit = "cover" | "contain";

/**
 * Pure function: resolves a PictureField to a display-ready URL string.
 * No network calls — just string construction for the img-cdn pipeline.
 *
 * Returns undefined when a gravatar record carries an unparseable URL, so
 * callers fall back to initials instead of throwing mid-render.
 */
export function resolveAvatarUrl(
    picture: PictureField,
    size: number,
    fit: AvatarFit = "cover",
    config: AvatarCdnConfig = DEFAULT_CDN_CONFIG,
): string | undefined {
    switch (picture.type) {
        case "legacy-cdn":
            return buildProcessedImageUrl(picture.key, size, fit, config);
        case "gravatar": {
            let url: URL;
            try {
                url = new URL(picture.url);
            } catch {
                return undefined;
            }
            url.searchParams.set("s", String(size));
            return url.toString();
        }
        case "url":
            return picture.url;
    }
}

/**
 * UTF-8-safe base64 encode. Native `btoa()` throws an InvalidCharacterError on
 * characters outside the Latin1 range (e.g. CJK asset keys), so encode the
 * string to UTF-8 bytes first. Mirrors `apps/site-search/src/utils/imageUrl.ts`.
 */
function toBase64(input: string): string {
    const bytes = new TextEncoder().encode(input);
    let binary = "";
    for (const byte of bytes) {
        binary += String.fromCharCode(byte);
    }
    return btoa(binary);
}

function buildProcessedImageUrl(key: string, size: number, fit: AvatarFit, config: AvatarCdnConfig): string {
    const imageRequest = {
        bucket: config.bucket,
        key,
        edits: {
            resize: {
                width: size,
                height: size,
                fit,
                background: {r: 0, g: 0, b: 0, alpha: 0},
            },
        },
        outputFormat: "webp",
    };
    return `${config.imgCdnUrl}/${toBase64(JSON.stringify(imageRequest))}`;
}
